// components/modals.js
// Shared modal components: InfoModal, DetailDrawer, DeclineModal, CancelModal, AdminRefundModal
// Commercial use: original IP

import React, { useState } from "react";
import { NAVY, BLUE, GREEN, RED, AMBER, STEEL, BORDER, TEXT2, MUTED, lbl, inp, card } from "../constants";
import { LOAD_TIERS, ZONES } from "../constants";
import { calcCancellationFee } from "../services/supabaseService";
import { getLatestPayment } from "../utils/paymentUtils";

// ── Info Modal — reusable centered modal for resident-facing messages ─────────
export function InfoModal({ title, message, onClose, actions, spinner }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, padding: 28, width: "100%", maxWidth: 380, textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: NAVY, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 14, color: STEEL, lineHeight: 1.6, marginBottom: spinner ? 20 : 24 }}>{message}</div>
        {spinner && <div style={{ width: 32, height: 32, border: "3px solid #dce4ef", borderTopColor: BLUE, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 20px" }} />}
        {actions && actions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {actions.map((a, i) => (
              <button key={i} onClick={a.onClick} style={{ width: "100%", padding: 13, borderRadius: 10, border: i === 0 ? "none" : `1.5px solid ${BORDER}`, background: i === 0 ? "#3D90D9" : "#fff", color: i === 0 ? "#fff" : STEEL, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>{a.label}</button>
            ))}
          </div>
        ) : !spinner && (
          <button onClick={onClose} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: "#3D90D9", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Got it</button>
        )}
      </div>
    </div>
  );
}

// ── Detail Drawer ──────────────────────────────────────────────────────────────
export function DetailDrawer({ r, onClose }) {
  const tier = LOAD_TIERS.find(t => t.key === r.size);
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 200 }} />
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: "16px 16px 0 0", zIndex: 201, padding: 24, maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: NAVY }}>Request Details</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: STEEL }}>x</button>
        </div>
        {[
          ["Address", r.addr],
          ["Zone", ZONES.find(z => z.key === r.zone)?.label || r.zone || "—"],
          ["Phone", r.phone],
          ["Item Type", r.type],
          ["Load Size", tier?.label],
          ["Date", r.date],
          ["Window", r.pickup_window],
          ["Quick Pickup", r.quick ? "Yes" : "No"],
          ["Status", r.status],
          ...(r.predicted_item_type ? [["Load Type", r.predicted_item_type]] : []),
          ...(r.predicted_packing_class ? [["Packing Class", r.predicted_packing_class]] : []),
          ...(r.large_nonstackable_count != null ? [["Large Items", String(r.large_nonstackable_count)]] : []),
          ...(r.bulky_booking_flag != null ? [["Bulky Flag", r.bulky_booking_flag ? "Yes" : "No"]] : []),
        ].map(([l, v]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{l}</span>
            <span style={{ fontSize: 13, color: NAVY, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>{v || "—"}</span>
          </div>
        ))}
        {(r.photo1_url || r.photo2_url) && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, color: STEEL, fontWeight: 700, marginBottom: 8 }}>PHOTOS</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[r.photo1_url, r.photo2_url].filter(Boolean).map((url, i) => (
                <div key={i} onClick={() => setLightbox(url)} style={{ flex: 1, cursor: "pointer", borderRadius: 10, overflow: "hidden", border: `1.5px solid ${BORDER}`, position: "relative" }}>
                  <img src={url} alt={`Photo ${i + 1}`} style={{ width: "100%", height: 130, objectFit: "cover", display: "block" }} />
                  <div style={{ position: "absolute", bottom: 4, right: 6, fontSize: 10, color: "#fff", background: "rgba(0,0,0,.45)", borderRadius: 4, padding: "2px 5px", fontWeight: 600 }}>Tap to expand</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {lightbox && (
          <div onClick={() => setLightbox(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <img src={lightbox} alt="Full size" style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 12, objectFit: "contain" }} />
            <button onClick={() => setLightbox(null)} style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,.15)", border: "none", color: "#fff", borderRadius: "50%", width: 36, height: 36, fontSize: 20, cursor: "pointer" }}>x</button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Decline Modal ──────────────────────────────────────────────────────────────
export function DeclineModal({ r, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 800, fontSize: 17, color: NAVY, marginBottom: 4 }}>Decline Request</div>
        <div style={{ fontSize: 12, color: STEEL, marginBottom: 16 }}>{r.addr}</div>
        <label style={lbl}>Reason for declining</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Tell the resident why..." style={{ ...inp, height: 80, resize: "none", marginBottom: 16 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", color: NAVY, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Cancel</button>
          <button onClick={async () => { setBusy(true); await onConfirm(reason); setBusy(false); }} style={{ flex: 1, padding: 14, borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{busy ? "Declining..." : "Decline Request"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Cancel Modal ───────────────────────────────────────────────────────────────
export function CancelModal({ r, onClose, onConfirm }) {
  const { canCancel, feePercent, feeAmount, refundAmount } = calcCancellationFee(r);
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const total                = getLatestPayment(r.payments)?.amount || 0;
  const feeMessage = r.status === "en_route"
    ? "Your Hauler is already on the way, so a 60% cancellation fee applies."
    : feePercent > 0
      ? "This cancellation is within 2 hours of your pickup window, so a 20% cancellation fee applies."
      : "There is no cancellation fee for this booking.";

  if (!canCancel) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
        <div style={{ background: "#fff", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto" }} onClick={e => e.stopPropagation()}>
          <div style={{ fontWeight: 800, fontSize: 17, color: NAVY, marginBottom: 8 }}>Cannot Cancel</div>
          <div style={{ fontSize: 13, color: STEEL, marginBottom: 16 }}>This request cannot be cancelled at this stage.</div>
          <button onClick={onClose} style={{ width: "100%", padding: 14, borderRadius: 10, border: "none", background: NAVY, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 800, fontSize: 17, color: NAVY, marginBottom: 4 }}>Cancel This Booking?</div>
        <div style={{ fontSize: 12, color: STEEL, marginBottom: 10 }}>{r.addr}</div>
        <div style={{ fontSize: 12, color: feePercent > 0 ? "#b45309" : "#166534", lineHeight: 1.5, marginBottom: 14, padding: "8px 12px", background: feePercent > 0 ? "#fffbeb" : "#f0fdf4", borderRadius: 8, border: feePercent > 0 ? "1px solid #fcd34d" : "1px solid #bbf7d0" }}>
          {feeMessage}
        </div>
        {refundAmount > 0 && (
          <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "14px 16px", marginBottom: 12, border: "1px solid #bbf7d0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Your refund</div>
            <div style={{ fontWeight: 900, fontSize: 22, color: "#166534" }}>${(refundAmount / 100).toFixed(2)}</div>
          </div>
        )}
        <div style={{ background: "#f6f9fc", borderRadius: 10, padding: 14, marginBottom: 10 }}>
          {[
            ["Total paid", "$" + (total / 100).toFixed(2)],
            ["Cancellation fee (" + feePercent + "%)", "$" + (feeAmount / 100).toFixed(2)],
            ["You receive", "$" + (refundAmount / 100).toFixed(2)],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,.06)" }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: NAVY, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginBottom: 16 }}>Your slot will be released to another resident.</div>
        <button onClick={onClose} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: "#3D90D9", color: "#ffffff", fontWeight: 700, fontSize: 14, cursor: "pointer", marginBottom: 8, display: "block" }}>Keep My Booking</button>
        <button onClick={async () => { setBusy(true); await onConfirm({ reason, feePercent, feeAmount, refundAmount }); setBusy(false); }} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: "#e74c3c", color: "#ffffff", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "block", marginBottom: 10 }}>{busy ? "Cancelling..." : feePercent > 0 ? `Cancel — fee $${(feeAmount / 100).toFixed(2)}` : `Cancel and receive $${(refundAmount / 100).toFixed(2)} refund`}</button>
        <details style={{ marginBottom: 4 }}><summary style={{ fontSize: 11, color: MUTED, cursor: "pointer", textAlign: "center" }}>Add a reason (optional)</summary>
          <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Optional reason..." style={{ ...inp, height: 60, resize: "none", marginTop: 8 }} />
        </details>
      </div>
    </div>
  );
}

// ── Admin Refund Modal ─────────────────────────────────────────────────────────
export function AdminRefundModal({ r, onClose, onSave }) {
  const total       = getLatestPayment(r.payments)?.amount || 0;
  const [fp, setFp] = useState(r.cancellation_fee_percent || 0);
  const fa          = Math.round(total * fp / 100);
  const ra          = total - fa;
  const [note, setNote] = useState("");
  const [rs, setRs]     = useState(r.refund_status || "pending");
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 800, fontSize: 17, color: NAVY, marginBottom: 4 }}>Manage Cancellation</div>
        <div style={{ fontSize: 12, color: STEEL, marginBottom: 16 }}>{r.addr}</div>
        <div style={{ background: "#f6f9fc", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          {[["Total", "$" + (total / 100).toFixed(2)], ["Reason", r.cancellation_reason || "—"]].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,.06)" }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{k}</span>
              <span style={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
        <label style={lbl}>Fee Override</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[0, 20, 40, 60, 100].map(p => (
            <div key={p} onClick={() => setFp(p)} style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 8, border: `1.5px solid ${fp === p ? BLUE : BORDER}`, background: fp === p ? "#edf4ff" : "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, color: fp === p ? BLUE : STEEL }}>{p}%</div>
          ))}
        </div>
        <div style={{ background: "#e3f2fd", borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>Fee</span><span style={{ fontSize: 13, color: RED, fontWeight: 700 }}>${(fa / 100).toFixed(2)}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>Refund</span><span style={{ fontSize: 13, color: GREEN, fontWeight: 700 }}>${(ra / 100).toFixed(2)}</span></div>
        </div>
        <label style={lbl}>Refund Status</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["pending", "issued", "denied"].map(s => (
            <div key={s} onClick={() => setRs(s)} style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 8, border: `1.5px solid ${rs === s ? BLUE : BORDER}`, background: rs === s ? "#edf4ff" : "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, color: rs === s ? BLUE : STEEL, textTransform: "capitalize" }}>{s}</div>
          ))}
        </div>
        <label style={lbl}>Override Note</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note why you are overriding..." style={{ ...inp, height: 70, resize: "none", marginBottom: 16 }} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", color: NAVY, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Close</button>
          <button onClick={async () => { setBusy(true); await onSave({ feePercent: fp, feeAmount: fa, refundAmount: ra, overrideReason: note, refundStatus: rs }); setBusy(false); }} style={{ flex: 1, padding: 14, borderRadius: 10, border: "none", background: BLUE, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{busy ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add-On Modal — declare added items before the pickup-day cutoff ───────────
// Owner rulings 2026-07-10: add-ons close at 8:00 AM on pickup day; declaring
// never charges the customer. The Hauler verifies the real load on site and
// any difference bills through the existing photo-review flow.
export function AddOnModal({ r, onClose, onSubmit }) {
  const [desc, setDesc] = useState("");
  const [pieces, setPieces] = useState(0);
  const [busy, setBusy] = useState(false);
  const ready = desc.trim().length > 0 && desc.trim().length <= 280 && !busy;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 320, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: "22px 20px", width: "100%", maxWidth: 400 }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: NAVY, marginBottom: 4 }}>Add items to this pickup</div>
        <div style={{ fontSize: 12, color: STEEL, lineHeight: 1.5, marginBottom: 14 }}>
          Stage the added items with the rest. Your Hauler verifies the final load at pickup, and any price difference bills after a quick photo review. Add-ons close at 8:00 AM on pickup day.
        </div>
        <div style={lbl}>What did you add?</div>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} maxLength={280} rows={3} placeholder="Example: two bags of clothes and a small bookshelf"
          style={{ ...inp, width: "100%", resize: "none", fontFamily: "inherit", fontSize: 14, boxSizing: "border-box" }} />
        <div style={{ ...lbl, marginTop: 12 }}>Added mattresses or box springs</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
          <button onClick={() => setPieces(p => Math.max(0, p - 1))} style={{ minWidth: 44, minHeight: 44, borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", fontSize: 18, fontWeight: 700, color: NAVY, cursor: "pointer" }}>−</button>
          <div style={{ fontWeight: 800, fontSize: 18, color: NAVY, minWidth: 24, textAlign: "center" }}>{pieces}</div>
          <button onClick={() => setPieces(p => Math.min(10, p + 1))} style={{ minWidth: 44, minHeight: 44, borderRadius: 10, border: `1.5px solid ${BORDER}`, background: "#fff", fontSize: 18, fontWeight: 700, color: NAVY, cursor: "pointer" }}>+</button>
          <div style={{ fontSize: 11, color: STEEL, lineHeight: 1.4 }}>Each piece adds the $15 dump fee. A box spring counts as its own piece.</div>
        </div>
        <button disabled={!ready} onClick={async () => { setBusy(true); try { await onSubmit(desc.trim(), pieces); } finally { setBusy(false); } }}
          style={{ width: "100%", marginTop: 16, padding: 14, borderRadius: 12, border: "none", background: ready ? BLUE : "#ccc", color: "#fff", fontWeight: 800, fontSize: 15, cursor: ready ? "pointer" : "not-allowed", minHeight: 44 }}>
          {busy ? "Saving…" : "Save add-on"}
        </button>
        <button onClick={onClose} style={{ width: "100%", marginTop: 8, padding: 12, borderRadius: 12, border: "none", background: "transparent", color: STEEL, fontWeight: 600, fontSize: 13, cursor: "pointer", minHeight: 44 }}>Cancel</button>
      </div>
    </div>
  );
}
